// externals
import { useSelector } from 'react-redux';

// libraries
import { BreakPoint } from '@makemydeal/dr-activities-common';

// selectors
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { useMediaQuery } from 'react-responsive';

export type BaseDealBreakpoint = {
    SMALL: string;
    MEDIUM: string;
    LARGE: string;
    PHONE: string;
    SCREEN_MD_MIN: string;
    SCREEN_SM_MIN: string;
    TABLET_PORTRAIT: string;
    SM: string;
    MD: string;
    LG: string;
    XL: string;
};

export enum BaseDealBreakPoints {
    LARGE = '976px',
    XS = '0px',
    SM = '480px',
    MD = '680px',
    LG = '960px',
    XL = '1168px',
    XXL = '1650px'
}

const INFINITE_BREAK_POINT = '9999px';

export const useBaseDealBreakpoint = ({
    summaryTab = false,
    isPaymentGridEnabled = false,
    ignoreMultipleActiveScope = false
}: {
    summaryTab?: boolean;
    isPaymentGridEnabled?: boolean;
    ignoreMultipleActiveScope?: boolean;
} = {}): BaseDealBreakpoint => {
    const isBreakdownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isTabletView = useMediaQuery({
        query: `(min-width: ${BaseDealBreakPoints.SM}) and (max-width: ${BaseDealBreakPoints.LARGE})`
    });

    const isDealCentralTabletView = isTabletView && isDealCentralExperience;

    const applyWideLayout = summaryTab ? isPaymentGridEnabled || (!isDealCentralTabletView && isBreakdownOpen) : isBreakdownOpen;
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario) && !ignoreMultipleActiveScope;

    const breakPoints = {
        SMALL: applyWideLayout ? BreakPoint.MEDIUM : BreakPoint.SMALL,
        MEDIUM: applyWideLayout ? BaseDealBreakPoints.XXL : BreakPoint.MEDIUM,
        LARGE: applyWideLayout ? BreakPoint.TABLET_PORTRAIT : BaseDealBreakPoints.LARGE,
        PHONE: applyWideLayout ? BreakPoint.MEDIUM : BreakPoint.PHONE,
        SCREEN_MD_MIN: applyWideLayout ? BreakPoint.TABLET_PORTRAIT : BreakPoint.SCREEN_MD_MIN,
        SCREEN_SM_MIN: applyWideLayout ? BreakPoint.SCREEN_MD_MIN : BreakPoint.SCREEN_SM_MIN,
        TABLET_PORTRAIT: applyWideLayout ? BaseDealBreakPoints.XXL : BreakPoint.TABLET_PORTRAIT,
        SM: applyWideLayout ? BaseDealBreakPoints.MD : BaseDealBreakPoints.SM,
        MD: applyWideLayout ? BaseDealBreakPoints.LG : BaseDealBreakPoints.MD,
        LG: applyWideLayout ? BaseDealBreakPoints.XL : BaseDealBreakPoints.LG,
        XL: applyWideLayout ? '1920px' : BaseDealBreakPoints.XL
    };

    if (!summaryTab) {
        return breakPoints;
    }

    if (isDealCentralMultiScenario) {
        return Object.keys(breakPoints).reduce((obj, key) => {
            obj[key as keyof BaseDealBreakpoint] = INFINITE_BREAK_POINT;
            return obj;
        }, {} as BaseDealBreakpoint);
    }

    return breakPoints;
};
